import * as types from 'src/store/mutation-types'

import { InviteClient } from 'src/api/Invites'
import { SubscriptionClient } from 'src/api/Subscription'
import Team from 'src/models/team'
import { TeamClient } from 'src/api/Teams'

// initial state
// shape: [{ id, quantity }]
const state = {
  teams: [],
  team: null
}

// getters
const getters = {
  teams: state => state.teams,
  team: state => state.team
}

const actions = {
  createTeam ({ commit }, data) {
    return new TeamClient()
      .include([
        'settings',
        'settings.default_email_address',
        'settings.default_address',
        'settings.default_phone_number',
        'settings.default_bank_details',
        'members',
        'open_invites'
      ])
      .create(data)
      .then(response => {
        let team = response.data.data

        commit(types.ADD_TEAM, team)

        return team
      })
  },

  updateTeam ({ commit }, { teamId, team }) {
    return new TeamClient()
      .include([
        'settings',
        'settings.default_email_address',
        'settings.default_address',
        'settings.default_phone_number',
        'settings.default_bank_details',
        'members',
        'open_invites',
        'subscription'
      ])
      .update(teamId, team)
      .then(response => {
        let team = response.data.data

        commit(types.UPDATE_TEAM, team)

        return team
      })
  },

  removeTeam ({ commit }, token) {
    return new TeamClient().confirmDeletion(token)
      .then(response => {
        let team = response.data.data

        commit(types.REMOVE_TEAM, team)
        commit(types.SET_ACTIVE_TEAM, null)

        return team
      })
  },

  selectTeam ({ commit, state }, { teamId }) {
    return new Promise((resolve) => {
      let team = state.teams.find(team => team.id === teamId)

      commit(types.SET_TEAM, team)

      resolve(true)
    })
  },

  updateTeamMember ({ commit }, { teamId, userId, role }) {
    return new TeamClient()
      .updateMember(teamId, userId, role)
      .then(() => {
        commit(types.UPDATE_TEAM_MEMBER, { teamId, userId, role })
      })
  },

  removeTeamMember ({ commit, rootGetters, state }, { team, userId }) {
    return new TeamClient()
      .removeMember(team.id, userId)
      .then(() => {
        if (rootGetters.user.id === userId) {
          commit(types.REMOVE_TEAM, team)
          commit(types.SET_ACTIVE_TEAM, state.teams.length > 0 ? state.teams[0] : null)
        } else {
          commit(types.REMOVE_TEAM_MEMBER, { teamId: team.id, userId })
        }

        return state.teams
      })
  },

  createTeamInvite ({ commit }, { teamId, invite }) {
    return new InviteClient()
      .create(teamId, invite)
      .then(response => {
        let invite = response.data.data

        commit(types.CREATE_TEAM_INVITATION, invite)

        return invite
      })
  },

  removeTeamInvite ({ commit }, id) {
    return new InviteClient()
      .remove(id)
      .then(() => {
        commit(types.REMOVE_TEAM_INVITATION, id)
      })
  },

  uploadLogo ({ commit }, { teamId, logo }) {
    return new TeamClient()
      .uploadLogo(teamId, logo)
      .then(response => {
        let logoUrl = response.data.data.settings.data.logo_url
        commit(types.SET_TEAM_LOGO, { teamId, logoUrl })
      })
  },

  removeLogo ({ commit }, teamId) {
    return new TeamClient()
      .deleteLogo(teamId)
      .then(() => commit(types.SET_TEAM_LOGO, { teamId, logoUrl: null }))
  },

  uploadWatermark ({ commit }, { teamId, watermark }) {
    return new TeamClient()
      .uploadWatermark(teamId, watermark)
      .then(response => {
        let watermarkUrl = response.data.data.settings.data.watermark_url
        commit(types.SET_TEAM_WATERMARK, { teamId, watermarkUrl })
      })
  },

  removeWatermark ({ commit }, teamId) {
    return new TeamClient()
      .deleteWatermark(teamId)
      .then(() => commit(types.SET_TEAM_WATERMARK, { teamId, watermarkUrl: null }))
  },

  createTeamAddress ({ commit }, { teamId, data }) {
    return new TeamClient()
      .createAddress(teamId, data)
      .then(response => {
        let address = response.data.data

        commit(types.UPDATE_TEAM_ADDRESS, { teamId, addressId: address.id, address })
      })
  },

  createTeamEmailAddress ({ commit }, { teamId, data }) {
    return new TeamClient()
      .createEmailAddress(teamId, data)
      .then(response => {
        let emailAddress = response.data.data

        commit(types.UPDATE_TEAM_EMAIL_ADDRESS, { teamId, emailAddressId: emailAddress.id, emailAddress })
      })
  },

  createTeamPhoneNumber ({ commit }, { teamId, data }) {
    return new TeamClient()
      .createPhoneNumber(teamId, data)
      .then(response => {
        let phoneNumber = response.data.data

        commit(types.UPDATE_TEAM_PHONE_NUMBER, { teamId, phoneNumberId: phoneNumber.id, phoneNumber })
      })
  },

  createTeamBankDetails ({ commit }, { teamId, data }) {
    return new TeamClient()
      .createBankDetails(teamId, data)
      .then(response => {
        let bankDetails = response.data.data

        commit(types.UPDATE_TEAM_BANK_DETAILS, { teamId, bankDetailsId: bankDetails.id, bankDetails })
      })
  },

  updateTeamAddress ({ commit }, { teamId, addressId, data }) {
    return new TeamClient()
      .updateAddress(teamId, addressId, data)
      .then(response => {
        let address = response.data.data

        commit(types.UPDATE_TEAM_ADDRESS, { teamId, addressId, address })
      })
  },

  updateTeamEmailAddress ({ commit }, { teamId, emailAddressId, data }) {
    return new TeamClient()
      .updateEmailAddress(teamId, emailAddressId, data)
      .then(response => {
        let emailAddress = response.data.data

        commit(types.UPDATE_TEAM_EMAIL_ADDRESS, { teamId, emailAddressId, emailAddress })
      })
  },

  updateTeamPhoneNumber ({ commit }, { teamId, phoneNumberId, data }) {
    return new TeamClient()
      .updatePhoneNumber(teamId, phoneNumberId, data)
      .then(response => {
        let phoneNumber = response.data.data

        commit(types.UPDATE_TEAM_PHONE_NUMBER, { teamId, phoneNumberId, phoneNumber })
      })
  },

  updateTeamBankDetails ({ commit }, { teamId, bankDetailsId, data }) {
    return new TeamClient()
      .updateBankDetails(teamId, bankDetailsId, data)
      .then(response => {
        let bankDetails = response.data.data

        commit(types.UPDATE_TEAM_BANK_DETAILS, { teamId, bankDetailsId, bankDetails })
      })
  },

  deleteTeamAddress ({ commit }, { teamId, addressId }) {
    return new TeamClient()
      .destroyAddresses(teamId, addressId)
      .then(response => {
        commit(types.UPDATE_TEAM_ADDRESS, { teamId, addressId: null, address: null })
      })
  },

  deleteTeamEmailAddress ({ commit }, { teamId, emailAddressId }) {
    return new TeamClient()
      .destroyEmailAddresses(teamId, emailAddressId)
      .then(response => {
        commit(types.UPDATE_TEAM_EMAIL_ADDRESS, { teamId, emailAddressId: null, emailAddress: null })
      })
  },

  deleteTeamPhoneNumber ({ commit }, { teamId, phoneNumberId }) {
    return new TeamClient()
      .destroyPhoneNumbers(teamId, phoneNumberId)
      .then(response => {
        commit(types.UPDATE_TEAM_PHONE_NUMBER, { teamId, phoneNumberId: null, phoneNumber: null })
      })
  },

  deleteTeamBankDetails ({ commit }, { teamId, bankDetailsId }) {
    return new TeamClient()
      .destroyBankDetails(teamId, bankDetailsId)
      .then(response => {
        commit(types.UPDATE_TEAM_BANK_DETAILS, { teamId, bankDetailsId: null, bankDetails: null })
      })
  },

  subscribeToPlan ({ commit }, { teamId, plan, source = undefined, couponCode = undefined }) {
    return new SubscriptionClient()
      .create(teamId, plan, source, couponCode)
      .then(response => {
        commit(types.UPDATE_SUBSCRIPTION, { teamId, subscription: response.data.data.subscription.data })
        commit(types.SET_SUBSCRIPTION_STATUS, { teamId, subscriptionStatus: true })
      })
  },

  enableSubscription ({ commit }, { teamId }) {
    return new TeamClient()
      .include([
        'settings',
        'settings.default_email_address',
        'settings.default_address',
        'settings.default_phone_number',
        'settings.default_bank_details',
        'members',
        'open_invites',
        'subscription'
      ])
      .get(teamId)
      .then(response => {
        commit(types.UPDATE_SUBSCRIPTION, { teamId, subscription: response.data.data[0].subscription.data })
        commit(types.SET_SUBSCRIPTION_STATUS, { teamId, subscriptionStatus: true })
      })
  },

  cancelSubscription ({ commit }, { teamId, confirmationPassword }) {
    return new SubscriptionClient()
      .cancel(teamId, confirmationPassword)
      .then(response => {
        commit(types.UPDATE_SUBSCRIPTION, { teamId, subscription: response.data.data.subscription.data })
      })
  },

  storePdfTemplate ({ commit }, { teamId, file }) {
    return new TeamClient()
      .include([
        'settings',
        'settings.default_email_address',
        'settings.default_address',
        'settings.default_phone_number',
        'settings.default_bank_details',
        'members',
        'open_invites',
        'subscription'
      ])
      .uploadPDFTemplate(teamId, file)
      .then(response => {
        commit(types.SET_TEAM_SETTINGS, { teamId, settings: response.data.data.settings.data })
      })
  },

  deletePdfTemplate ({ commit }, teamId) {
    return new TeamClient()
      .deletePDFTemplate(teamId)
      .then(response => {
        commit(types.SET_TEAM_SETTINGS, { teamId, settings: response.data.data.settings.data })
      })
  }
}

// mutations
const mutations = {
  [types.SET_TEAMS] (state, teams) {
    state.teams = teams.map(team => new Team(team))
  },

  [types.SET_TEAM] (state, team) {
    state.team = team
  },

  [types.ADD_TEAM] (state, team) {
    state.teams.push(new Team(team))
  },

  [types.UPDATE_TEAM] (state, newTeam) {
    let teamIndex = state.teams.findIndex(team => team.id === newTeam.id)
    state.teams.splice(teamIndex, 1, new Team(newTeam))
  },

  [types.REMOVE_TEAM] (state, removedTeam) {
    let teamIndex = state.teams.findIndex(team => team.id === removedTeam.id)
    state.teams.splice(teamIndex, 1)
  },

  [types.UPDATE_TEAM_MEMBER] (state, { teamId, userId, role }) {
    let idx = state.teams.find(team => team.id === teamId).members.findIndex(member => member.id === userId)
    let member = state.teams.find(team => team.id === teamId).members[idx]
    member.role_id = role

    state.teams.find(team => team.id === teamId).members.splice(idx, 1, member)
  },

  [types.REMOVE_TEAM_MEMBER] (state, { teamId, userId }) {
    let idx = state.teams.find(team => team.id === teamId).members.findIndex(member => member.id === userId)
    state.teams.find(team => team.id === teamId).members.splice(idx, 1)
  },

  [types.CREATE_TEAM_INVITATION] (state, invite) {
    state.teams.find(team => team.id === invite.team_id).invites.push(invite)
  },

  [types.REMOVE_TEAM_INVITATION] (state, id) {
    let idx = state.teams.findIndex(team => team.invites.find(invite => invite.id === id))

    state.teams[idx].invites.splice(state.teams[idx].invites.findIndex(invite => invite.id === id), 1)
  },

  [types.SET_TEAM_LOGO] (state, { teamId, logoUrl }) {
    state.teams.find(team => team.id === teamId).settings.logo_url = logoUrl
  },

  [types.SET_TEAM_WATERMARK] (state, { teamId, watermarkUrl }) {
    state.teams.find(team => team.id === teamId).settings.watermark_url = watermarkUrl
  },

  [types.UPDATE_TEAM_ADDRESS] (state, { teamId, addressId, address }) {
    // Since we currently only support one address per team, we'll just update the default address
    let team = state.teams.find(team => team.id === teamId)

    team.settings.default_address_id = addressId
    team.settings.default_address = address
  },

  [types.UPDATE_TEAM_EMAIL_ADDRESS] (state, { teamId, emailAddressId, emailAddress }) {
    // Since we currently only support one email address per team, we'll just update the default email address
    let team = state.teams.find(team => team.id === teamId)

    team.settings.default_email_address_id = emailAddressId
    team.settings.default_email_address = emailAddress
  },

  [types.UPDATE_TEAM_PHONE_NUMBER] (state, { teamId, phoneNumberId, phoneNumber }) {
    // Since we currently only support one phone number per team, we'll just update the default phone number
    let team = state.teams.find(team => team.id === teamId)

    team.settings.default_phone_number_id = phoneNumberId
    team.settings.default_phone_number = phoneNumber
  },

  [types.UPDATE_TEAM_BANK_DETAILS] (state, { teamId, bankDetailsId, bankDetails }) {
    // Since we currently only support "one" bank details per team, we'll just update the default bank details
    let team = state.teams.find(team => team.id === teamId)

    team.settings.default_bank_details_id = bankDetailsId
    team.settings.default_bank_details = bankDetails
  },

  [types.UPDATE_SUBSCRIPTION] (state, { teamId, subscription }) {
    let team = state.teams.find(team => team.id === teamId)

    team.subscription = subscription
  },

  [types.SET_SUBSCRIPTION_STATUS] (state, { teamId, subscriptionStatus }) {
    let team = state.teams.find(team => team.id === teamId)

    team.subscribed = subscriptionStatus
  },

  [types.SET_TEAM_SETTINGS] (state, { teamId, settings }) {
    let team = state.teams.find(team => team.id === teamId)

    team.settings = settings
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
